import React, { useState } from 'react';
import { Wallet, Shield, ExternalLink, Mail } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Label } from '@vaiu/ui/label';
import { Input } from '@vaiu/ui/input';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { PaymentMethod, platformApi } from '@/services/platformApi';

interface PayPalConnectProps {
  onSubmit: (method: PaymentMethod) => Promise<void>;
  loading: boolean;
  onCancel: () => void;
}

const PayPalConnect: React.FC<PayPalConnectProps> = ({
  onSubmit,
  loading,
  onCancel
}) => {
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [connectType, setConnectType] = useState<'oauth' | 'manual'>('manual');

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleManualConnect = async () => {
    if (!paypalEmail.trim()) {
      setError('Please enter your PayPal email address');
      return;
    }

    if (!isValidEmail(paypalEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setPaypalLoading(true);
      setError(null);

      const paypalData = {
        email: paypalEmail.trim(),
        paypalUserId: `manual_${Date.now()}`,
        accountId: `paypal_${Date.now()}`,
        connectionType: 'manual'
      };

      await handlePayPalSuccess(paypalData);
    } catch (err: any) {
      setError(err.message || 'Failed to connect PayPal account');
    } finally {
      setPaypalLoading(false);
    }
  };

  const handlePayPalConnect = async () => {
    try {
      setPaypalLoading(true);
      setError(null);

      // In a real implementation, you would initialize PayPal connection here
      // This would typically involve:
      // 1. Redirecting to PayPal OAuth
      // 2. Getting authorization from user
      // 3. Exchanging auth code for access token
      
      // For demo purposes, we'll simulate the process
      setTimeout(() => {
        handlePayPalSuccess({
          email: 'user@example.com',
          paypalUserId: 'paypal_user_123',
          accessToken: 'demo_access_token',
          connectionType: 'oauth'
        });
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to connect PayPal account');
    } finally {
      setPaypalLoading(false);
    }
  };

  const handlePayPalSuccess = async (paypalData: any) => {
    try {
      // Connect PayPal account through backend - this saves to backend
      const savedPaymentMethod = await platformApi.connectPayPalAccount(paypalData);
      
      // Update the isDefault preference if needed
      if (setAsDefault && !savedPaymentMethod.isDefault) {
        // Set as default via API call
        await platformApi.setDefaultPaymentMethod(savedPaymentMethod.id!);
        savedPaymentMethod.isDefault = true;
      }

      // Pass the actual saved payment method to parent
      await onSubmit(savedPaymentMethod);
    } catch (err: any) {
      setError(err.message || 'Failed to connect PayPal account');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Connection Type Selector */}
      <div className="space-y-3">
        <Label className="text-base font-medium">How would you like to connect PayPal?</Label>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant={connectType === 'manual' ? 'default' : 'outline'}
            onClick={() => setConnectType('manual')}
            className="h-auto p-4 flex flex-col items-center space-y-2"
          >
            <Mail className="w-5 h-5" />
            <div className="text-center">
              <div className="font-medium">Enter Email</div>
              <div className="text-xs opacity-70">Manual entry</div>
            </div>
          </Button>
          <Button
            type="button"
            variant={connectType === 'oauth' ? 'default' : 'outline'}
            onClick={() => setConnectType('oauth')}
            className="h-auto p-4 flex flex-col items-center space-y-2"
          >
            <ExternalLink className="w-5 h-5" />
            <div className="text-center">
              <div className="font-medium">OAuth</div>
              <div className="text-xs opacity-70">Secure login</div>
            </div>
          </Button>
        </div>
      </div>

      {connectType === 'manual' ? (
        <div className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Enter your PayPal email:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• This will be used for PayPal payouts</li>
              <li>• Make sure it's your verified PayPal email</li>
              <li>• We'll send transfers to this address</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paypal-email">PayPal Email Address</Label>
            <Input
              id="paypal-email"
              type="email"
              placeholder="your.email@example.com"
              value={paypalEmail}
              onChange={(e) => setPaypalEmail(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="setAsDefault"
              checked={setAsDefault}
              onCheckedChange={(checked) => setSetAsDefault(checked as boolean)}
            />
            <Label htmlFor="setAsDefault" className="text-sm">
              Set as default payment method
            </Label>
          </div>

          <Button
            onClick={handleManualConnect}
            disabled={loading || paypalLoading || !paypalEmail.trim()}
            className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
          >
            {paypalLoading ? (
              'Connecting...'
            ) : (
              <>
                <Wallet className="w-4 h-4 mr-2" />
                Connect PayPal Email
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Connect via PayPal OAuth:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Secure authentication through PayPal</li>
              <li>• Access your PayPal balance and linked accounts</li>
              <li>• Automatic verification of account ownership</li>
            </ul>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="setAsDefault"
              checked={setAsDefault}
              onCheckedChange={(checked) => setSetAsDefault(checked as boolean)}
            />
            <Label htmlFor="setAsDefault" className="text-sm">
              Set as default payment method
            </Label>
          </div>

          <Button
            onClick={handlePayPalConnect}
            disabled={loading || paypalLoading}
            className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
          >
            {paypalLoading ? (
              'Connecting...'
            ) : (
              <>
                <Wallet className="w-4 h-4 mr-2" />
                Connect with PayPal OAuth
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}

      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Shield className="w-4 h-4" />
        <span>Secured by PayPal's industry-leading protection</span>
      </div>

      <div className="text-xs text-muted-foreground text-center">
        By connecting PayPal, you agree to PayPal's{' '}
        <a href="#" className="underline">User Agreement</a> and{' '}
        <a href="#" className="underline">Privacy Policy</a>
      </div>

      <div className="flex space-x-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading || paypalLoading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default PayPalConnect; 